/**
 * useTemplates Hook
 *
 * Manages the list of user templates and template operations
 * including fetching, reordering, and CRUD operations.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAccessToken, useUser } from '@features/user';
import APIService from '@services/APIService';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { PublicTemplateLoader, UserTemplateLoader } from '../../../loaders/TemplatesLoader';
import type {
  AdminPublicTemplate,
  AvailableModule,
  LoadedTemplate,
  ModuleMappings,
  TemplateWithMeta,
  UserTemplateItem,
} from '../types';

interface UseTemplatesReturn {
  /** List of user templates with metadata */
  templates: TemplateWithMeta[];
  /** Whether templates are loading */
  isLoading: boolean;
  /** Currently selected template data */
  selectedTemplate: LoadedTemplate | null;
  /** Whether selected template is loading */
  isTemplateLoading: boolean;
  /** Available modules for adding sections */
  availableModules: AvailableModule[];
  /** Module mappings for customizations */
  moduleMappings: ModuleMappings | undefined;
  /** Public templates for library */
  publicTemplates: AdminPublicTemplate[];
  /** Select a template by ID */
  selectTemplate: (template: UserTemplateItem | null) => void;
  /** Reorder templates list */
  reorderTemplates: (startIndex: number, endIndex: number) => Promise<void>;
  /** Create new template from scratch */
  createFromScratch: () => Promise<string | null>;
  /** Copy user template */
  copyUserTemplate: (templateId: string) => Promise<string | null>;
  /** Copy public template to user */
  copyPublicTemplate: (templateId: string) => Promise<string | null>;
  /** Add public template to user's list */
  addPublicTemplate: (templateId: string) => Promise<string | null>;
  /** Delete user template */
  deleteTemplate: (templateId: string) => Promise<boolean>;
  /** Remove public template from user's list */
  removePublicTemplate: (templateId: string) => Promise<boolean>;
  /** Refresh templates list */
  refresh: () => Promise<boolean>;
  /** Check if template can be modified */
  canModifyTemplate: (template: UserTemplateItem) => boolean;
}

export const useTemplates = (): UseTemplatesReturn => {
  const { getAccessToken } = useAccessToken();
  const { templatesList, refreshTemplatesList } = useUser();
  const { template: templateMetrics } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [selectedTemplate, setSelectedTemplate] = useState<LoadedTemplate | null>(null);
  const [isTemplateLoading, setIsTemplateLoading] = useState(false);
  const [availableModules, setAvailableModules] = useState<AvailableModule[]>([]);
  const [moduleMappings, setModuleMappings] = useState<ModuleMappings>();
  const [publicTemplates, setPublicTemplates] = useState<AdminPublicTemplate[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Transform templates list to include metadata
  const templates = useMemo((): TemplateWithMeta[] => {
    if (!templatesList) return [];

    return templatesList.map(template => ({
      ...template,
      canModify: template.type === 'user',
      canDuplicate: template.type !== 'group',
      canDelete: template.type !== 'group',
      iconType: template.type === 'user' ? 'user' : template.type === 'group' ? 'group' : 'public',
    }));
  }, [templatesList]);

  const isLoading = templatesList === undefined || isInitialLoading;

  // Fetch available modules
  const fetchAvailableModules = useCallback(async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const response = await APIService.makeAPIGetRequest({
      requestString: '/template/getAvailableModules',
      accessToken,
    });

    if (response.ok) {
      setAvailableModules((response.value as { modules: AvailableModule[] }).modules);
    }
  }, [getAccessToken]);

  // Fetch module mappings
  const fetchModuleMappings = useCallback(async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const response = await APIService.makeAPIGetRequest({
      requestString: '/template/getModuleMappings',
      accessToken,
    });

    if (response.ok) {
      setModuleMappings((response.value as { module_mappings: ModuleMappings }).module_mappings);
    }
  }, [getAccessToken]);

  // Fetch public templates
  const fetchPublicTemplates = useCallback(async () => {
    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const response = await APIService.makeAPIGetRequest({
      requestString: '/template/getAllPublicTemplates',
      accessToken,
    });

    if (response.ok) {
      setPublicTemplates((response.value as { templates: AdminPublicTemplate[] }).templates);
    }
  }, [getAccessToken]);

  // Load selected template data
  const loadTemplate = useCallback(async (templateItem: UserTemplateItem) => {
    setIsTemplateLoading(true);

    try {
      let template: LoadedTemplate;

      if (templateItem.type === 'user') {
        template = await UserTemplateLoader({
          params: { templateId: templateItem.template_id },
        });
      } else if (templateItem.type === 'public') {
        template = await PublicTemplateLoader({
          params: { templateId: templateItem.template_id },
        });
      } else if (templateItem.type === 'group') {
        const templateId = templateItem.template_id;
        if (templateId.startsWith('u:')) {
          template = await UserTemplateLoader({ params: { templateId } });
        } else if (templateId.startsWith('p:')) {
          template = await PublicTemplateLoader({ params: { templateId } });
        } else {
          throw new Error('Invalid group template ID');
        }
      } else {
        throw new Error('Unknown template type');
      }

      setSelectedTemplate(template);
    } catch (error) {
      console.error('Failed to load template:', error);
      enqueueSnackbar('Failed to load template', { variant: 'error' });
      setSelectedTemplate(null);
    } finally {
      setIsTemplateLoading(false);
    }
  }, [enqueueSnackbar]);

  // Select template
  const selectTemplate = useCallback((template: UserTemplateItem | null) => {
    if (template) {
      void loadTemplate(template);
    } else {
      setSelectedTemplate(null);
    }
  }, [loadTemplate]);

  // Reorder templates
  const reorderTemplates = useCallback(async (startIndex: number, endIndex: number) => {
    if (!templatesList) return;

    const accessToken = await getAccessToken();
    if (!accessToken) return;

    const result = Array.from(templatesList);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    await APIService.makeAPIPostRequest({
      requestString: '/user/updateUserTemplates',
      accessToken,
      body: { user_templates: result },
    });

    refreshTemplatesList();
  }, [templatesList, getAccessToken, refreshTemplatesList]);

  // Create from scratch
  const createFromScratch = useCallback(async (): Promise<string | null> => {
    const accessToken = await getAccessToken();
    if (!accessToken) return null;

    const response = await APIService.makeAPIPostRequest({
      requestString: '/template/createUserTemplate',
      accessToken,
      body: {
        template_name: '[Name Your Template]',
        summary_modules: [],
        sections: [],
      },
    });

    if (response.ok) {
      refreshTemplatesList();
      return (response.value as { template_id: string }).template_id;
    }

    enqueueSnackbar('Failed to create template', { variant: 'error' });
    return null;
  }, [getAccessToken, refreshTemplatesList, enqueueSnackbar]);

  // Copy user template
  const copyUserTemplate = useCallback(async (templateId: string): Promise<string | null> => {
    const accessToken = await getAccessToken();
    if (!accessToken) return null;

    const response = await APIService.makeAPIPostRequest({
      requestString: '/template/copyUserTemplate',
      accessToken,
      body: { template_id: templateId },
    });

    if (response.ok) {
      refreshTemplatesList();
      return (response.value as { template_id: string }).template_id;
    }

    enqueueSnackbar('Failed to copy template', { variant: 'error' });
    return null;
  }, [getAccessToken, refreshTemplatesList, enqueueSnackbar]);

  // Copy public template
  const copyPublicTemplate = useCallback(async (templateId: string): Promise<string | null> => {
    const accessToken = await getAccessToken();
    if (!accessToken) return null;

    const response = await APIService.makeAPIPostRequest({
      requestString: '/template/copyPublicTemplateToUser',
      accessToken,
      body: { template_id: templateId },
    });

    if (response.ok) {
      refreshTemplatesList();
      return (response.value as { template_id: string }).template_id;
    }

    enqueueSnackbar('Failed to copy template', { variant: 'error' });
    return null;
  }, [getAccessToken, refreshTemplatesList, enqueueSnackbar]);

  // Add public template
  const addPublicTemplate = useCallback(async (templateId: string): Promise<string | null> => {
    const accessToken = await getAccessToken();
    if (!accessToken) return null;

    const response = await APIService.makeAPIPostRequest({
      requestString: '/template/addPublicTemplateToUser',
      accessToken,
      body: { public_template_id: templateId },
    });

    if (response.ok) {
      refreshTemplatesList();
      return (response.value as { template_id: string }).template_id;
    }

    enqueueSnackbar('Failed to add template', { variant: 'error' });
    return null;
  }, [getAccessToken, refreshTemplatesList, enqueueSnackbar]);

  // Delete template
  const deleteTemplate = useCallback(async (templateId: string): Promise<boolean> => {
    const accessToken = await getAccessToken();
    if (!accessToken) return false;

    const response = await APIService.makeAPIPostRequest({
      requestString: '/template/deleteUserTemplate',
      accessToken,
      body: { template_id: templateId },
    });

    if (response.ok) {
      templateMetrics.delete(templateId, { template_type: 'user', is_custom: true });
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      refreshTemplatesList();
      return true;
    }

    enqueueSnackbar('Failed to delete template', { variant: 'error' });
    return false;
  }, [getAccessToken, refreshTemplatesList, enqueueSnackbar, templateMetrics]);

  // Remove public template
  const removePublicTemplate = useCallback(async (templateId: string): Promise<boolean> => {
    const accessToken = await getAccessToken();
    if (!accessToken) return false;

    const response = await APIService.makeAPIPostRequest({
      requestString: '/template/removePublicTemplate',
      accessToken,
      body: { template_id: templateId },
    });

    if (response.ok) {
      templateMetrics.delete(templateId, { template_type: 'public', is_custom: false });
      enqueueSnackbar('Template removed successfully', { variant: 'success' });
      refreshTemplatesList();
      return true;
    }

    enqueueSnackbar('Failed to remove template', { variant: 'error' });
    return false;
  }, [getAccessToken, refreshTemplatesList, enqueueSnackbar, templateMetrics]);

  // Check if template can be modified
  const canModifyTemplate = useCallback((template: UserTemplateItem): boolean => {
    return template.type === 'user';
  }, []);

  // Initial data fetch
  useEffect(() => {
    const initialize = async () => {
      await Promise.all([
        refreshTemplatesList(),
        fetchAvailableModules(),
        fetchPublicTemplates(),
        fetchModuleMappings(),
      ]);
      setIsInitialLoading(false);
    };

    void initialize();
  }, [refreshTemplatesList, fetchAvailableModules, fetchPublicTemplates, fetchModuleMappings]);

  return {
    templates,
    isLoading,
    selectedTemplate,
    isTemplateLoading,
    availableModules,
    moduleMappings,
    publicTemplates,
    selectTemplate,
    reorderTemplates,
    createFromScratch,
    copyUserTemplate,
    copyPublicTemplate,
    addPublicTemplate,
    deleteTemplate,
    removePublicTemplate,
    refresh: refreshTemplatesList,
    canModifyTemplate,
  };
};
